import React, { useState, useEffect, useRef } from 'react';
import { 
  Tooltip, 
  Button, 
  Select, 
  ColorPicker
} from 'antd';
import { 
  FormOutlined, 
  FontSizeOutlined, 
  BorderOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { Resizable } from 're-resizable';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { Option } = Select;

// Add CSS styles just once at module level
const initializeQuillStyles = () => {
  const styleId = 'quill-custom-styles';
  if (!document.getElementById(styleId)) {
    const styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.innerHTML = `
      /* Size labels */
      .ql-snow .ql-picker.ql-size .ql-picker-label::before { content: 'Size'; }
      
      /* Size dropdown items */
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="small"]::before { content: 'Small'; }
      .ql-snow .ql-picker.ql-size .ql-picker-item:not([data-value])::before { content: 'Normal'; }
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="large"]::before { content: 'Large'; }
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="huge"]::before { content: 'Huge'; }
      
      /* Size in dropdown label after selection */
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="small"]::before { content: 'Small'; }
      .ql-snow .ql-picker.ql-size .ql-picker-label:not([data-value])::before { content: 'Normal'; }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="large"]::before { content: 'Large'; }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="huge"]::before { content: 'Huge'; }
      
      /* Fix toolbar appearance */
      .ql-toolbar.ql-snow { 
        display: none;
        position: absolute;
        top: -40px;
        left: 0;
        z-index: 10;
        background: white;
        border-radius: 4px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important;
        padding: 8px !important;
        transition: opacity 0.2s ease;
        border: none !important;
      }
      
      /* Show toolbar when selected */
      .text-annotation-selected .ql-toolbar.ql-snow,
      .sticky-note-selected .ql-toolbar.ql-snow {
        display: flex;
        flex-wrap: wrap;
      }
      
      /* Fix container styling */
      .ql-container.ql-snow {
        border: none !important;
        background: transparent !important;
        height: auto !important;
        overflow: visible !important;
      }
      
      /* Fix editor styling */
      .ql-editor {
        background: transparent !important;
        padding: 0 !important;
        overflow: visible !important;
        height: auto !important;
        min-height: 30px;
      }
      
      /* Font size styles for the editor */
      .ql-editor .ql-size-small {
        font-size: 12px !important;
      }
      
      .ql-editor {
        font-size: 16px !important;
      }
      
      .ql-editor .ql-size-large {
        font-size: 20px !important;
      }
      
      .ql-editor .ql-size-huge {
        font-size: 24px !important;
      }
      
      /* More visible placeholder text */
      .ql-editor.ql-blank::before {
        color: #999 !important;
        font-style: normal !important;
        font-size: 14px !important;
      }
    `;
    document.head.appendChild(styleEl);
    return true;
  }
  return false;
};

// Initialize styles immediately
initializeQuillStyles();

const resizeHandleStyles = {
  bottom: {
    bottom: -4,
    height: '8px',
    cursor: 'row-resize'
  },
  right: {
    right: -4,
    width: '8px',
    cursor: 'col-resize'
  },
  bottomRight: {
    right: -4,
    bottom: -4,
    width: '12px',
    height: '12px',
    cursor: 'nwse-resize'
  }
};

// Delete button component that appears on hover
const DeleteButton = ({ onClick }) => (
  <div
    style={{
      position: 'absolute',
      top: -12,
      right: -12,
      backgroundColor: '#ff4d4f',
      borderRadius: '50%',
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      opacity: 0,
      transition: 'opacity 0.2s',
      zIndex: 10,
    }}
    className="delete-button nodrag"
    onClick={(e) => {
      e.stopPropagation();
      if (typeof onClick === 'function') {
        onClick();
      }
    }}
  >
    <DeleteOutlined style={{ color: 'white', fontSize: '12px' }} />
  </div>
);

const AnnotationToolbar = () => {
  return (
    <div style={{
      position: 'absolute',
      right: '20px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      background: 'white',
      padding: '8px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      zIndex: 5
    }}>
      <Tooltip title="Drag to add Sticky Note" placement="left">
        <Button 
          icon={<FormOutlined />}
          className="nodrag annotation-button"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('application/reactflow', 'stickyNote');
            e.dataTransfer.effectAllowed = 'move';
          }}
          style={{ cursor: 'grab' }}
        />
      </Tooltip>
      <Tooltip title="Drag to add Text" placement="left">
        <Button 
          icon={<FontSizeOutlined />}
          className="nodrag annotation-button"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('application/reactflow', 'textAnnotation');
            e.dataTransfer.effectAllowed = 'move';
          }}
          style={{ cursor: 'grab' }}
        />
      </Tooltip>
      <Tooltip title="Drag to add Shape" placement="left">
        <Button 
          icon={<BorderOutlined />}
          className="nodrag annotation-button"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('application/reactflow', 'shape');
            e.dataTransfer.effectAllowed = 'move';
          }}
          style={{ cursor: 'grab' }}
        />
      </Tooltip>
    </div>
  );
};

const StickyNote = ({ data, id, selected }) => {
  // Use data values as initial state
  const defaultText = '<p>Add your note here...</p>';
  const [content, setContent] = useState(data.content || defaultText);
  const [color, setColor] = useState(data.color || '#fff9c4');
  const [size, setSize] = useState(data.size || { width: 200, height: 150 });
  const contentRef = useRef(null);
  
  // Update component state when data prop changes
  useEffect(() => {
    // Only update if data.content exists and is not empty
    if (data.content) {
      setContent(data.content);
    }
    setColor(data.color || '#fff9c4');
    setSize(data.size || { width: 200, height: 150 });
  }, [data.content, data.color, data.size]);

  // Rich text editor modules for sticky notes
  const quillModules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
      ]
    }
  };

  // Auto-resize when content changes - with safeguards
  useEffect(() => {
    const timer = setTimeout(() => {
      if (contentRef.current) {
        const qlEditor = contentRef.current.querySelector('.ql-editor');
        if (qlEditor) {
          const contentHeight = qlEditor.scrollHeight;
          setSize(prev => ({ ...prev, height: Math.max(prev.height, contentHeight + 40) }));
        }
      }
    }, 100); // Longer timeout to ensure DOM is ready
    
    return () => clearTimeout(timer);
  }, [content]);

  const handleResize = (e, direction, ref, d) => {
    const newSize = {
      width: size.width + d.width,
      height: size.height + d.height
    };
    setSize(newSize);
    // Call onChange to update the parent component with the new size
    if (typeof data.onChange === 'function') {
      data.onChange(id, { size: newSize });
    }
  };

  const handleRichTextChange = (value) => {
    setContent(value);
    if (typeof data.onChange === 'function') {
      data.onChange(id, { content: value });
    }
  };

  return (
    <Resizable
      size={size}
      onResizeStop={handleResize}
      minWidth={150}
      minHeight={100}
      handleStyles={resizeHandleStyles}
      handleClasses={{ bottom: 'nodrag', right: 'nodrag', bottomRight: 'nodrag' }}
      className="react-flow-annotation"
      style={{ position: 'relative', zIndex: -1, isolation: 'isolate' }}
    >
      <div 
        className={`sticky-note ${selected ? 'sticky-note-selected' : ''}`}
        style={{
          backgroundColor: color,
          padding: '15px',
          height: '100%',
          boxShadow: '3px 3px 8px rgba(0,0,0,0.1)',
          borderRadius: '3px',
          fontSize: '14px',
          border: selected ? '2px solid #1890ff' : '1px solid #e8e8e8',
          cursor: 'move',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          const deleteButton = e.currentTarget.querySelector('.delete-button');
          if (deleteButton) deleteButton.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          const deleteButton = e.currentTarget.querySelector('.delete-button');
          if (deleteButton) deleteButton.style.opacity = '0';
        }}
      >
        <DeleteButton onClick={() => {
          if (typeof data.onDelete === 'function') {
            data.onDelete(id);
          }
        }} />
        
        <div className="nodrag" style={{ 
            minHeight: '100px', 
            background: 'transparent',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'stretch',
            marginBottom: '8px'
          }}>
          <div ref={contentRef} style={{ height: '100%', background: 'transparent', width: '100%' }}>
            <ReactQuill 
              value={content}
              defaultValue={defaultText}
              placeholder="Add your note here..."
              onChange={handleRichTextChange}
              modules={quillModules}
              theme="snow"
              style={{ 
                flex: 1,
                border: 'none',
                background: 'transparent'
              }}
            />
          </div>
        </div>
        
        <div style={{ marginTop: '8px', display: 'flex', gap: '4px' }}>
          {['#fff9c4', '#f8bbd0', '#b3e5fc', '#c8e6c9', '#e1bee7'].map((c) => (
            <div
              key={c}
              onClick={() => {
                setColor(c);
                if (typeof data.onChange === 'function') {
                  data.onChange(id, { color: c });
                }
              }}
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: c,
                borderRadius: '50%',
                cursor: 'pointer',
                border: color === c ? '2px solid #1890ff' : '1px solid #e8e8e8'
              }}
              className="nodrag color-picker"
            />
          ))}
        </div>
      </div>
    </Resizable>
  );
};

const TextAnnotation = ({ data, id, selected }) => {
  const defaultText = '<p>Type your text here</p>';
  const [content, setContent] = useState(data.content || defaultText);
  const [size, setSize] = useState(data.size || { width: 300, height: 'auto' });
  const contentRef = useRef(null);
  const [editorHeight, setEditorHeight] = useState('auto');

  // Update component state when data prop changes
  useEffect(() => {
    // Only update if data.content exists and is not empty
    if (data.content) {
      setContent(data.content);
    }
    setSize(data.size || { width: 300, height: 'auto' });
  }, [data.content, data.size]);

  // Auto-resize when content changes - with safeguards
  useEffect(() => {
    const timer = setTimeout(() => {
      if (contentRef.current) {
        const qlEditor = contentRef.current.querySelector('.ql-editor');
        if (qlEditor) {
          const contentHeight = qlEditor.scrollHeight;
          setEditorHeight(`${contentHeight + 20}px`);
        }
      }
    }, 100); // Longer timeout to ensure DOM is ready
    
    return () => clearTimeout(timer);
  }, [content]);

  const handleResize = (e, direction, ref, d) => {
    const newSize = {
      width: size.width + d.width,
      height: 'auto'
    };
    setSize(newSize);
    
    // Recalculate the height after resize
    const timer = setTimeout(() => {
      if (contentRef.current) {
        const qlEditor = contentRef.current.querySelector('.ql-editor');
        if (qlEditor) {
          const contentHeight = qlEditor.scrollHeight;
          setEditorHeight(`${contentHeight + 20}px`);
        }
      }
    }, 100);
    
    // Call onChange to update the parent component with the new size
    if (typeof data.onChange === 'function') {
      data.onChange(id, { size: newSize });
    }
    
    return () => clearTimeout(timer);
  };

  // Rich text editor modules
  const quillModules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
      ]
    }
  };

  const handleRichTextChange = (value) => {
    setContent(value);
    if (typeof data.onChange === 'function') {
      data.onChange(id, { content: value });
    }
  };

  return (
    <Resizable
      size={size}
      onResizeStop={handleResize}
      minWidth={150}
      handleStyles={resizeHandleStyles}
      handleClasses={{ right: 'nodrag' }}
      enable={{ right: true, bottom: false, bottomRight: false }}
      className="react-flow-annotation"
      style={{ position: 'relative', zIndex: -1, isolation: 'isolate', height: 'auto' }}
    >
      <div 
        className={`text-annotation ${selected ? 'text-annotation-selected' : ''}`}
        style={{
          border: selected ? '2px solid #1890ff' : '1px solid transparent',
          padding: '8px',
          borderRadius: '4px',
          cursor: 'move',
          position: 'relative',
          background: 'transparent',
          minHeight: '50px'
        }}
        onMouseEnter={(e) => {
          const deleteButton = e.currentTarget.querySelector('.delete-button');
          if (deleteButton) deleteButton.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          const deleteButton = e.currentTarget.querySelector('.delete-button');
          if (deleteButton) deleteButton.style.opacity = '0';
        }}
      >
        <DeleteButton onClick={() => {
          if (typeof data.onDelete === 'function') {
            data.onDelete(id);
          }
        }} />
        
        <div className="nodrag" style={{ 
          minHeight: '50px', 
          background: 'transparent',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'stretch',
          width: '100%'
        }}>
          <div ref={contentRef} style={{ height: editorHeight, background: 'transparent', width: '100%' }}>
            <ReactQuill 
              value={content}
              defaultValue={defaultText}
              placeholder="Type your text here"
              onChange={handleRichTextChange}
              modules={quillModules}
              theme="snow"
              style={{ 
                flex: 1,
                border: 'none',
                background: 'transparent'
              }}
            />
          </div>
        </div>
      </div>
    </Resizable>
  );
};

const Shape = ({ data, id, selected }) => {
  const [shapeType, setShapeType] = useState(data.shapeType || 'rectangle');
  const [color, setColor] = useState(data.color || '#e8e8e8');
  const [size, setSize] = useState(data.size || { width: 100, height: 100 });

  // Update component state when data prop changes
  useEffect(() => {
    setShapeType(data.shapeType || 'rectangle');
    setColor(data.color || '#e8e8e8');
    setSize(data.size || { width: 100, height: 100 });
  }, [data.shapeType, data.color, data.size]);

  const handleResize = (e, direction, ref, d) => {
    const newSize = {
      width: size.width + d.width,
      height: size.height + d.height
    };
    setSize(newSize);
    // Call onChange to update the parent component with the new size
    if (typeof data.onChange === 'function') {
      data.onChange(id, { size: newSize });
    }
  };

  // Function to get a darker shade of a color
  const getDarkerShade = (hexColor) => {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    // Darken by reducing each component by 20%
    const darkerR = Math.max(0, Math.floor(r * 0.8));
    const darkerG = Math.max(0, Math.floor(g * 0.8));
    const darkerB = Math.max(0, Math.floor(b * 0.8));
    
    // Convert back to hex
    return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
  };

  const borderColor = selected ? '#1890ff' : getDarkerShade(color);
  
  const shapeStyles = {
    rectangle: {
      width: '100%',
      height: '100%',
      backgroundColor: color,
      border: selected ? '2px solid #1890ff' : `1px solid ${borderColor}`
    },
    circle: {
      width: '100%',
      height: '100%',
      backgroundColor: color,
      borderRadius: '50%',
      border: selected ? '2px solid #1890ff' : `1px solid ${borderColor}`
    },
    triangle: {
      width: '100%',
      height: '100%',
      clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
      backgroundColor: color,
      boxShadow: selected ? 'none' : `inset 0 0 0 1px ${borderColor}`
    }
  };

  return (
    <Resizable
      size={size}
      onResizeStop={handleResize}
      minWidth={30}
      minHeight={30}
      handleStyles={resizeHandleStyles}
      handleClasses={{ bottom: 'nodrag', right: 'nodrag', bottomRight: 'nodrag' }}
      className="react-flow-annotation"
      style={{ position: 'relative', zIndex: -1, isolation: 'isolate' }}
    >
      <div style={{ 
        height: '100%',
        cursor: 'move',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        const deleteButton = e.currentTarget.querySelector('.delete-button');
        if (deleteButton) deleteButton.style.opacity = '1';
      }}
      onMouseLeave={(e) => {
        const deleteButton = e.currentTarget.querySelector('.delete-button');
        if (deleteButton) deleteButton.style.opacity = '0';
      }}
      >
        <DeleteButton onClick={() => {
          if (typeof data.onDelete === 'function') {
            data.onDelete(id);
          }
        }} />
        {selected && (
          <div style={{ 
            marginBottom: '8px',
            position: 'absolute',
            top: -40,
            left: 0,
            background: 'white',
            padding: '4px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1
          }} className="nodrag">
            <Select
              value={shapeType}
              onChange={(value) => {
                setShapeType(value);
                if (typeof data.onChange === 'function') {
                  data.onChange(id, { shapeType: value });
                }
              }}
              style={{ width: '120px', marginRight: '8px' }}
              className="nodrag"
            >
              <Option value="rectangle">Rectangle</Option>
              <Option value="circle">Circle</Option>
              <Option value="triangle">Triangle</Option>
            </Select>
            <ColorPicker
              value={color}
              onChange={(value) => {
                const hexColor = value.toHexString();
                setColor(hexColor);
                if (typeof data.onChange === 'function') {
                  data.onChange(id, { color: hexColor });
                }
              }}
              className="nodrag"
            />
          </div>
        )}
        <div style={shapeStyles[shapeType]} />
      </div>
    </Resizable>
  );
};

export { AnnotationToolbar, StickyNote, TextAnnotation, Shape };